import styled from '@emotion/styled';

import {
  spacing,
  borderRadius,
  typography,
  transitions,
  colors,
} from '../tokens';

export type DayStatus = 'route' | 'off-route' | 'unavailable';

/**
 * Paleta + rótulo por status do dia, lida do Figma (node 10187-55095).
 * Mantida local ao componente: divergem dos tokens semânticos globais.
 *
 * Mapeamento da semântica legada (src/components/Granel/calendar):
 *  suggest-date  → route        ("Dia de rota")
 *  open-date     → off-route    ("Fora de rota")
 *  invalid-date  → unavailable  ("Indisponivel")
 */
export const statusSpec: Record<
  DayStatus,
  { badgeBg: string; badgeText: string; label: string }
> = {
  route: {
    badgeBg: '#0B29FB',
    badgeText: '#FFFFFF',
    label: 'Dia de rota',
  },
  'off-route': {
    badgeBg: '#262626',
    badgeText: '#FFFFFF',
    label: 'Fora de rota',
  },
  unavailable: {
    badgeBg: '#E8E8E8',
    badgeText: '#A6A6A6',
    label: 'Indisponivel',
  },
};

const ACCENT = '#0B29FB';
const TEXT_DEFAULT = '#3D3D3D';
const TEXT_WEEKDAY = '#6B6B6B';
const TEXT_DISABLED = '#A6A6A6';

/** Cor do texto do dia conforme status e seleção (evita ternário aninhado). */
const dayTextColor = (
  status: DayStatus,
  selected: boolean,
  base: string
): string => {
  if (status === 'unavailable') return TEXT_DISABLED;
  if (selected) return ACCENT;
  return base;
};

// ─── Root: [ < ]  faixa de dias  [ > ] ────────────────────────────────────────

export const CalendarRoot = styled('div', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $error?: boolean }>`
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: ${spacing.md};
  width: 100%;
  box-sizing: border-box;
  ${({ $error }) =>
    $error
      ? `outline: 1px solid ${colors.error}; outline-offset: 6px; border-radius: 8px;`
      : ''}
`;

export const NavButton = styled('button')`
  flex-shrink: 0;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1.5px solid ${ACCENT};
  border-radius: ${borderRadius.full};
  background: ${colors.background};
  color: ${ACCENT};
  cursor: pointer;
  transition: background ${transitions.fast}, opacity ${transitions.fast};

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: #eef1ff;
  }

  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }

  &:disabled {
    border-color: ${colors.border};
    color: ${colors.mediumGray};
    cursor: default;
    opacity: 0.6;
  }
`;

// ─── Faixa de dias ────────────────────────────────────────────────────────────

export const DaysStrip = styled('div')`
  display: flex;
  align-items: stretch;
  justify-content: safe center;
  gap: ${spacing.md};
  flex: 0 1 auto;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  padding-bottom: ${spacing.xs};

  /* scrollbar discreta */
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.border};
    border-radius: ${borderRadius.full};
  }
`;

// ─── Card de dia ──────────────────────────────────────────────────────────────

export const DayCard = styled('button', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $status: DayStatus; $selected: boolean; $today?: boolean }>`
  flex: 0 0 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  overflow: hidden;
  border-radius: ${borderRadius.lg};
  border: ${({ $selected, $today }) => {
    if ($selected) return `2px solid ${ACCENT}`;
    if ($today) return '1px solid #262626';
    return `1px solid ${colors.border}`;
  }};
  background: ${({ $selected }) => ($selected ? '#EEF1FF' : colors.background)};
  cursor: ${({ $status }) =>
    $status === 'unavailable' ? 'default' : 'pointer'};
  transition: border-color ${transitions.fast}, background ${transitions.fast},
    box-shadow ${transitions.fast};
  font-family: ${typography.fontFamily.primary};

  &:hover:not(:disabled) {
    border-color: ${ACCENT};
  }

  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
  }
`;

export const DayCardTop = styled('span')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 13px;
`;

export const Weekday = styled('span', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $status: DayStatus; $selected: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.bold};
  line-height: 1.2;
  white-space: nowrap;
  color: ${({ $status, $selected }) =>
    dayTextColor($status, $selected, TEXT_WEEKDAY)};
`;

export const DayNumber = styled('span', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $status: DayStatus; $selected: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: 28px;
  font-weight: ${typography.fontWeight.extraBold};
  line-height: 1.1;
  color: ${({ $status, $selected }) =>
    dayTextColor($status, $selected, TEXT_DEFAULT)};
`;

export const StatusBadge = styled('span', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $status: DayStatus }>`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 32px;
  padding: ${spacing.sm} ${spacing.xs};
  background: ${({ $status }) => statusSpec[$status].badgeBg};
  color: ${({ $status }) => statusSpec[$status].badgeText};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  line-height: 1.2;
  white-space: nowrap;
`;

export const EmptyMessage = styled('p')`
  flex: 1;
  margin: 0;
  padding: ${spacing.lg};
  text-align: center;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.md};
  color: ${colors.textSecondary};
`;
